import { useState } from 'react';
import {
  Chip, IconButton, Tooltip, Stack, TextField, MenuItem, Menu, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Divider, Box, Alert, Snackbar,
} from '@mui/material';
import { Visibility, MoreVert, Add } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../../components/PageHeader';
import { DataTable, type Column } from '../../../components/DataTable';
import { vendorOnboardingApi, OPERATIONAL_STATUSES, type VendorApplication, type ReviewDecision, type OperationalStatus } from '../../../api/vendorOnboarding.api';
import { extractApiError } from '../../../api/error';
import { useAuth } from '../../../hooks/useAuth';

const STAGE_DECISIONS: Record<string, ReviewDecision[]> = {
  document_verification: ['verify', 'reject', 'request_clarification', 'send_back'],
  technical: ['approve', 'reject', 'conditional', 'request_clarification', 'send_back'],
  commercial: ['approve', 'reject', 'conditional', 'request_clarification', 'send_back'],
  final: ['approve', 'reject', 'conditional', 'send_back'],
};
const DECISION_LABEL: Record<ReviewDecision, string> = { verify: 'Verify', approve: 'Approve', reject: 'Reject', conditional: 'Conditional Approval', request_clarification: 'Request Clarification', send_back: 'Send Back for Correction' };
const DECISION_COLOR: Record<ReviewDecision, 'success' | 'error' | 'warning' | 'info'> = { verify: 'success', approve: 'success', reject: 'error', conditional: 'warning', request_clarification: 'info', send_back: 'warning' };

const statusColor = (s: string): 'default' | 'success' | 'error' | 'warning' | 'info' =>
  ['approved', 'active'].includes(s) ? 'success' : s === 'rejected' ? 'error'
    : ['clarification_required', 'conditional_approval', 'sent_back', 'withdrawn'].includes(s) ? 'warning' : 'info';
const opColor = (s: string): 'default' | 'success' | 'error' | 'warning' =>
  s === 'active' ? 'success' : s === 'blacklisted' ? 'error' : s === 'inactive' ? 'default' : 'warning';
const human = (s?: string | null) => (s ? s.replace(/_/g, ' ') : '—');

const PENDING = ['submitted', 'under_review', 'document_verification_pending', 'technical_evaluation_pending', 'commercial_evaluation_pending', 'final_approval_pending', 'clarification_required'];

export function VendorApplicationsPage() {
  const { hasPermission } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [status, setStatus] = useState('');
  const [reviewId, setReviewId] = useState<number | null>(null);
  const [statusFor, setStatusFor] = useState<VendorApplication | null>(null);
  const [disableFor, setDisableFor] = useState<VendorApplication | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const canCreate = hasPermission('vendor_onboarding.create');
  const canReview = hasPermission('vendor_onboarding.review');
  const canSubmit = hasPermission('vendor_onboarding.submit');
  const canDisable = hasPermission('vendor_onboarding.disable_user');
  const canStatus = hasPermission('vendor_onboarding.change_status');

  const { data, isLoading } = useQuery({ queryKey: ['vendor-applications', page, limit, status], queryFn: () => vendorOnboardingApi.list({ page, limit, status: status || undefined }) });
  const refresh = () => qc.invalidateQueries({ queryKey: ['vendor-applications'] });
  const run = (p: Promise<unknown>, msg: string) => p.then(() => { setToast(msg); refresh(); }).catch((e) => setToast(extractApiError(e)));

  const columns: Column<VendorApplication>[] = [
    { key: 'application_code', label: 'Vendor Code', width: 140 },
    { key: 'legal_name', label: 'Vendor Name' },
    { key: 'primary_vendor_type', label: 'Vendor Type', render: (r) => r.primary_vendor_type ?? '—' },
    { key: 'contact_person', label: 'Contact', render: (r) => (r.contact_person ? <Box><Typography variant="body2">{r.contact_person}</Typography><Typography variant="caption" color="text.secondary">{r.contact_mobile ?? r.contact_email ?? ''}</Typography></Box> : '—') },
    { key: 'source_type', label: 'Source', render: (r) => r.source_type ?? '—' },
    { key: 'current_stage', label: 'Stage', render: (r) => human(r.current_stage) },
    { key: 'status', label: 'Status', render: (r) => <Chip size="small" color={statusColor(r.status)} label={human(r.status)} /> },
    { key: 'operational_status', label: 'Operational', render: (r) => (r.vendor_id ? <Chip size="small" color={opColor(r.operational_status ?? 'active')} label={human(r.operational_status ?? 'active')} /> : '—') },
    { key: 'submitted_at', label: 'Submitted', render: (r) => (r.submitted_at ? new Date(r.submitted_at).toLocaleDateString() : '—') },
  ];

  return (
    <>
      <PageHeader title="Vendor Applications" subtitle="The single source of truth for vendor onboarding — Draft → Verification → Approval → Activation → Operations."
        action={canCreate && <Button startIcon={<Add />} variant="contained" onClick={() => setCreateOpen(true)}>New Application</Button>} />

      <Stack direction="row" mb={2}>
        <TextField select size="small" label="Status" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} sx={{ minWidth: 240 }}>
          <MenuItem value="">All</MenuItem>
          {['draft', 'sent_back', 'document_verification_pending', 'clarification_required', 'technical_evaluation_pending', 'commercial_evaluation_pending', 'final_approval_pending', 'approved', 'active', 'rejected', 'withdrawn'].map((s) => <MenuItem key={s} value={s}>{human(s)}</MenuItem>)}
        </TextField>
      </Stack>

      <DataTable columns={columns} rows={data?.data ?? []} loading={isLoading} page={page} limit={limit} total={data?.meta.total ?? 0}
        onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }} rowKey={(r) => r.id}
        actions={(r) => (
          <RowActions
            row={r}
            perms={{ canReview, canSubmit, canDisable, canStatus, canCreate }}
            onView={() => setReviewId(r.id)}
            onEdit={() => navigate(`/vendor-applications/${r.id}/edit`)}
            onSubmit={() => run(vendorOnboardingApi.submit(r.id), 'Submitted for review')}
            onWithdraw={() => run(vendorOnboardingApi.withdraw(r.id), 'Application withdrawn')}
            onDelete={() => run(vendorOnboardingApi.remove(r.id), 'Draft deleted')}
            onReopen={() => run(vendorOnboardingApi.reopen(r.id), 'Application reopened')}
            onClone={() => vendorOnboardingApi.clone(r.id).then((a) => { setToast('Cloned to a new draft'); refresh(); navigate(`/vendor-applications/${a.id}/edit`); }).catch((e) => setToast(extractApiError(e)))}
            onDisable={() => setDisableFor(r)}
            onEnable={() => run(vendorOnboardingApi.enableUser(r.id), 'Vendor login re-enabled')}
            onChangeStatus={() => setStatusFor(r)}
          />
        )} />

      {reviewId && <ReviewDialog id={reviewId} canReview={canReview} onClose={() => setReviewId(null)} />}
      {statusFor && <StatusDialog app={statusFor} onClose={() => setStatusFor(null)} onDone={() => { setStatusFor(null); setToast('Operational status updated'); refresh(); }} />}
      {disableFor && <DisableDialog app={disableFor} onClose={() => setDisableFor(null)} onDone={() => { setDisableFor(null); setToast('Vendor login disabled'); refresh(); }} />}
      {createOpen && <CreateDialog onClose={() => setCreateOpen(false)} onCreated={(id) => { setCreateOpen(false); refresh(); navigate(`/vendor-applications/${id}/edit`); }} />}

      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}>
        <Alert severity="info" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </>
  );
}

interface Perms { canReview: boolean; canSubmit: boolean; canDisable: boolean; canStatus: boolean; canCreate: boolean }

function RowActions({ row, perms, onView, onEdit, onSubmit, onWithdraw, onDelete, onReopen, onClone, onDisable, onEnable, onChangeStatus }: {
  row: VendorApplication; perms: Perms;
  onView: () => void; onEdit: () => void; onSubmit: () => void; onWithdraw: () => void; onDelete: () => void;
  onReopen: () => void; onClone: () => void; onDisable: () => void; onEnable: () => void; onChangeStatus: () => void;
}) {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const close = () => setAnchor(null);
  const s = row.status;
  const isDraft = s === 'draft' || s === 'sent_back' || s === 'clarification_required';
  const isPending = PENDING.includes(s);
  const isActive = s === 'approved' || s === 'active';
  const isDead = s === 'rejected' || s === 'withdrawn';

  const items: Array<{ label: string; onClick: () => void; color?: string }> = [];
  if (isDraft && perms.canSubmit) { items.push({ label: 'Edit', onClick: onEdit }); if (s !== 'clarification_required') items.push({ label: 'Submit', onClick: onSubmit }); items.push({ label: 'Delete', onClick: onDelete, color: 'error' }); }
  if (isPending) { if (perms.canReview) items.push({ label: 'Review', onClick: onView }); if (perms.canSubmit) items.push({ label: 'Withdraw', onClick: onWithdraw, color: 'warning' }); }
  if (isActive) {
    if (perms.canStatus) items.push({ label: 'Change Status', onClick: onChangeStatus });
    if (perms.canDisable) items.push(row.user_disabled ? { label: 'Enable User', onClick: onEnable } : { label: 'Disable User', onClick: onDisable, color: 'error' });
  }
  if (isDead) { if (perms.canReview) items.push({ label: 'Reopen', onClick: onReopen }); if (perms.canCreate) items.push({ label: 'Clone', onClick: onClone }); }

  return (
    <>
      <Tooltip title="View"><IconButton size="small" onClick={onView}><Visibility fontSize="small" /></IconButton></Tooltip>
      {items.length > 0 && (
        <>
          <IconButton size="small" onClick={(e) => setAnchor(e.currentTarget)}><MoreVert fontSize="small" /></IconButton>
          <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={close}>
            {items.map((it) => <MenuItem key={it.label} onClick={() => { close(); it.onClick(); }} sx={it.color ? { color: `${it.color}.main` } : undefined}>{it.label}</MenuItem>)}
          </Menu>
        </>
      )}
    </>
  );
}

function ReviewDialog({ id, canReview, onClose }: { id: number; canReview: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [remarks, setRemarks] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { data: app } = useQuery({ queryKey: ['vendor-application', id], queryFn: () => vendorOnboardingApi.get(id) });

  const reviewMut = useMutation({
    mutationFn: (decision: ReviewDecision) => vendorOnboardingApi.review(id, decision, remarks || undefined),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['vendor-applications'] }); qc.invalidateQueries({ queryKey: ['vendor-application', id] }); setRemarks(''); setError(null); },
    onError: (e) => setError(extractApiError(e)),
  });

  const decisions = app && STAGE_DECISIONS[app.current_stage] ? STAGE_DECISIONS[app.current_stage] : [];
  const terminal = app ? ['approved', 'active', 'rejected'].includes(app.status) : false;

  return (
    <Dialog open fullWidth maxWidth="md" onClose={onClose}>
      <DialogTitle>{app ? `${app.application_code} — ${app.legal_name}` : 'Loading…'}</DialogTitle>
      <DialogContent dividers>
        {app && (
          <Stack spacing={2}>
            {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
            <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
              <Field label="Type" value={app.primary_vendor_type} />
              <Field label="Source" value={`${app.source_type ?? '—'}${app.submitted_role ? ` (${human(app.submitted_role)})` : ''}`} />
              <Field label="GST" value={app.gst_number} />
              <Field label="PAN" value={app.pan_number} />
              <Field label="Contact" value={app.contact_person ? `${app.contact_person} · ${app.contact_mobile ?? app.contact_email ?? ''}` : null} />
              <Box><Typography variant="caption" color="text.secondary">Status</Typography><Box><Chip size="small" color={statusColor(app.status)} label={human(app.status)} /></Box></Box>
              {app.vendor_id ? <Box><Typography variant="caption" color="text.secondary">Operational</Typography><Box><Chip size="small" color={opColor(app.operational_status ?? 'active')} label={human(app.operational_status ?? 'active')} /></Box></Box> : null}
            </Stack>

            <Divider />
            <Typography variant="subtitle2">Approval workflow</Typography>
            <Stack spacing={0.5}>
              {(app.workflow ?? []).map((w) => (
                <Stack key={w.id} direction="row" spacing={1} alignItems="center">
                  <Chip size="small" label={`${w.stage_order}. ${human(w.stage)}`} variant="outlined" />
                  <Chip size="small" color={statusColor(w.status)} label={human(w.status)} />
                  {w.remarks && <Typography variant="caption" color="text.secondary">— {w.remarks}</Typography>}
                </Stack>
              ))}
            </Stack>

            <Divider />
            <Typography variant="subtitle2">Internal evaluation (admin only)</Typography>
            <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
              <Field label="Doc Verification" value={app.document_verification_status} />
              <Field label="Technical" value={app.technical_evaluation_status} />
              <Field label="Commercial" value={app.commercial_evaluation_status} />
              <Field label="Final" value={app.final_status} />
            </Stack>

            {canReview && !terminal && decisions.length > 0 && (
              <>
                <Divider />
                <Typography variant="subtitle2">Action — {human(app.current_stage)} stage</Typography>
                <TextField size="small" label="Remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} multiline minRows={2} />
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {decisions.map((d) => (
                    <Button key={d} variant="contained" color={DECISION_COLOR[d]} disabled={reviewMut.isPending} onClick={() => reviewMut.mutate(d)}>{DECISION_LABEL[d]}</Button>
                  ))}
                </Stack>
              </>
            )}
            {terminal && <Alert severity={app.status === 'rejected' ? 'error' : 'success'}>This application is {human(app.status)}{app.vendor_id ? ` — vendor master #${app.vendor_id} created.` : '.'}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}

function Field({ label, value }: { label: string; value?: string | null }) {
  return <Box><Typography variant="caption" color="text.secondary">{label}</Typography><Typography>{value ?? '—'}</Typography></Box>;
}

function StatusDialog({ app, onClose, onDone }: { app: VendorApplication; onClose: () => void; onDone: () => void }) {
  const [statusVal, setStatusVal] = useState<OperationalStatus>(app.operational_status ?? 'active');
  const [reason, setReason] = useState('');
  const [remarks, setRemarks] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const mut = useMutation({
    mutationFn: () => vendorOnboardingApi.changeOperationalStatus(app.id, { status: statusVal, reason: reason || undefined, remarks: remarks || undefined, effectiveDate: effectiveDate || undefined }),
    onSuccess: onDone, onError: (e) => setError(extractApiError(e)),
  });
  return (
    <Dialog open fullWidth maxWidth="xs" onClose={onClose}>
      <DialogTitle>Change Operational Status — {app.application_code}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} mt={1}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField select label="Status" value={statusVal} onChange={(e) => setStatusVal(e.target.value as OperationalStatus)}>
            {OPERATIONAL_STATUSES.map((s) => <MenuItem key={s} value={s}>{human(s)}</MenuItem>)}
          </TextField>
          <TextField label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} />
          <TextField label="Effective Date" type="date" InputLabelProps={{ shrink: true }} value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} />
          <TextField label="Remarks" multiline minRows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={mut.isPending} onClick={() => mut.mutate()}>Update</Button>
      </DialogActions>
    </Dialog>
  );
}

function DisableDialog({ app, onClose, onDone }: { app: VendorApplication; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const mut = useMutation({ mutationFn: () => vendorOnboardingApi.disableUser(app.id, reason || undefined), onSuccess: onDone, onError: (e) => setError(extractApiError(e)) });
  return (
    <Dialog open fullWidth maxWidth="xs" onClose={onClose}>
      <DialogTitle>Disable Vendor Login — {app.application_code}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} mt={1}>
          {error && <Alert severity="error">{error}</Alert>}
          <Alert severity="warning">The vendor will be signed out and blocked from logging in until re-enabled.</Alert>
          <TextField label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} multiline minRows={2} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="error" disabled={mut.isPending} onClick={() => mut.mutate()}>Disable</Button>
      </DialogActions>
    </Dialog>
  );
}

function CreateDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (id: number) => void }) {
  const [legalName, setLegalName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const mut = useMutation({ mutationFn: () => vendorOnboardingApi.create({ legal_name: legalName || 'New Vendor' }), onSuccess: (a) => onCreated(a.id), onError: (e) => setError(extractApiError(e)) });
  return (
    <Dialog open fullWidth maxWidth="xs" onClose={onClose}>
      <DialogTitle>New Vendor Application</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} mt={1}>
          {error && <Alert severity="error">{error}</Alert>}
          <Typography variant="body2" color="text.secondary">Start a draft; you'll be taken to the onboarding wizard to complete the details.</Typography>
          <TextField label="Company Legal Name" value={legalName} onChange={(e) => setLegalName(e.target.value)} autoFocus />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={mut.isPending} onClick={() => mut.mutate()}>Create & Continue</Button>
      </DialogActions>
    </Dialog>
  );
}
